import { jwtDecode } from "jwt-decode";

import { isJwtExpired } from "./jwt-expiry";

export const SESSION_ENDED_MESSAGE = "Your session ended. Sign in again.";

/** Normalize an axios URL to a path without a leading slash or query. */
export function requestPath(url: string | undefined): string {
  if (!url) return "";
  let path = url;
  try {
    if (/^https?:\/\//i.test(url)) {
      path = new URL(url).pathname;
    }
  } catch {
    // Keep the raw string when URL parsing fails.
  }
  return (path.replace(/^\//, "").split("?")[0] ?? "").replace(/\/$/, "");
}

/**
 * Auth routes that return 401 for bad credentials while signing in.
 * Clearing the store on those would bounce the user mid-login.
 */
export function isCredentialAuthRequest(url: string | undefined): boolean {
  const path = requestPath(url);
  return (
    path === "auth/login" ||
    path === "auth/register" ||
    path === "auth/guest" ||
    path.startsWith("auth/verify/") ||
    path === "auth/resend-verification-email" ||
    path.startsWith("auth/password-reset/") ||
    path.startsWith("auth/firebase") ||
    path.startsWith("auth/google") ||
    path.startsWith("auth/github")
  );
}

/** Exact GET /datasources list — not /datasources/:id/... */
export function isDatasourcesListRequest(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if ((method ?? "get").toLowerCase() !== "get") return false;
  return requestPath(url) === "datasources";
}

/**
 * True when the request Authorization bearer JWT has an `exp` in the past.
 */
export function authorizationBearerExpired(
  authorization: string | null | undefined,
): boolean {
  if (typeof authorization !== "string") return false;
  const match = /^Bearer\s+(\S+)/i.exec(authorization.trim());
  if (!match) return false;
  try {
    const decoded = jwtDecode<{ exp?: number }>(match[1]);
    return isJwtExpired(decoded);
  } catch {
    return false;
  }
}

/**
 * Clear the session only for a dead JWT or a 401 on the signed-in list call.
 * Other authorized 401s (Sheets reconnect, preview-as, guest share) stay put.
 */
export function shouldClearSessionOn401(args: {
  status?: number;
  url?: string;
  method?: string;
  authorization?: string | null;
}): boolean {
  if (args.status !== 401) return false;
  if (!args.authorization || !String(args.authorization).trim()) return false;
  if (isCredentialAuthRequest(args.url)) return false;
  if (authorizationBearerExpired(args.authorization)) return true;
  if (isDatasourcesListRequest(args.method, args.url)) return true;
  return false;
}

let handlingUnauthorized = false;

/**
 * Drop the persisted session and send the user to sign-in.
 * Guest-share keys live in sessionStorage and are left alone.
 * useAuth is required lazily to avoid a get-api ↔ useAuth cycle.
 */
export function endSessionFromUnauthorized(): void {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useAuth } = require("./useAuth") as typeof import("./useAuth");
    const state = useAuth.getState();
    if (!state.token && !state.isAuthenticated) return;
    state.logout();
    state.setError(SESSION_ENDED_MESSAGE);
    if (typeof window === "undefined") return;
    const hashPath = (window.location.hash.replace(/^#/, "") || "/").split(
      "?",
    )[0];
    if (hashPath === "/auth" || hashPath.startsWith("/auth/")) return;
    window.location.hash = "#/auth";
  } finally {
    queueMicrotask(() => {
      handlingUnauthorized = false;
    });
  }
}

/** Test helper: reset the in-flight guard between cases. */
export function __resetUnauthorizedSessionGuard(): void {
  handlingUnauthorized = false;
}
